import { gql } from '@apollo/client';

export const GET_USER = `
  query GetUser($userId: ID!) {
    getUser(userId: $userId) {
      userId
      email
      name
      userType
      profileImage
      bio
      website
      nickname
      phoneNumber
      phoneVerified
      marketingConsentCollection
      marketingConsentPromotion
      marketingConsentEmail
      marketingConsentSMS
      oauthProvider
      kakaoId
      naverId
      googleId
      facebookId
      createdAt
      updatedAt
    }
  }
`;

export const GET_USERS_ADMIN = `
  query GetUsersAdmin($filters: AdminUsersFiltersInput) {
    getUsersAdmin(filters: $filters) {
      users {
        userId
        email
        name
        userType
        status
        oauthProvider
        socialChannels {
          platform
          account
          followerCount
        }
        hasSocialLinked
        createdAt
      }
      count
      total
      page
      pageSize
      stats {
        total
        influencer
        brand
        agency
        dailyNew
        monthlyNew
      }
    }
  }
`;

export const GET_USER_DETAIL_ADMIN = `
  query GetUserDetailAdmin($userId: ID!) {
    getUserDetailAdmin(userId: $userId) {
      userId
      email
      name
      profileImage
      userType
      status
      oauthProvider
      createdAt
      updatedAt
      lastLoginAt
      campaignCount
      hostId
      influencerId
      withdrawnAt
      withdrawalReasons
      withdrawalFeedback
      social {
        instagram { account followerCount }
        blog { account followerCount }
        youtube { account followerCount }
        tiktok { account followerCount }
        naverblog { account followerCount }
        twitter { account followerCount }
      }
    }
  }
`;

// Host Profile Queries
export const GET_HOST_PROFILE = `
  query GetHostProfile($hostId: ID!) {
    getHostProfile(hostId: $hostId) {
      hostId
      userId
      companyName
      contactEmail
      contactName
      contactPhone
      industry
      businessType
      companySize
      website
      address
      city
      country
      description
      establishedYear
      businessRegistrationNumber
      preferredCampaignTypes
      budgetRange
      isVerified
      latitude
      longitude
      createdAt
      updatedAt
    }
  }
`;

export const GET_HOST_PROFILE_BY_USER_ID = `
  query GetHostProfileByUserId($userId: ID!) {
    getHostProfileByUserId(userId: $userId) {
      success
      message
      error
      data {
        hostId
        userId
        companyName
        contactEmail
        contactName
        contactPhone
        contactPhoneVerified
        industry
        businessType
        companySize
        website
        address
        addressLine1
        addressLine2
        city
        state
        postalCode
        country
        description
        establishedYear
        businessRegistrationNumber
        registrationNumber
        registrationVerified
        registrationFileUrl
        preferredCampaignTypes
        budgetRange
        isVerified
        latitude
        longitude
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_HOST_PROFILES = `
  query GetHostProfiles($limit: Int) {
    getHostProfiles(limit: $limit) {
      hostProfiles {
        hostId
        userId
        companyName
        contactEmail
        contactName
        contactPhone
        industry
        businessType
        companySize
        website
        address
        city
        country
        description
        establishedYear
        businessRegistrationNumber
        preferredCampaignTypes
        budgetRange
        isVerified
        createdAt
        updatedAt
      }
      count
      total
    }
  }
`;

export const GET_INFLUENCERS = `
  query GetInfluencers($filters: InfluencerFiltersInput) {
    getInfluencers(filters: $filters) {
      influencers {
        influencerId
        userId
        name
        email
        categories
        mainCategory
        engagementRate
        rating
        ratePerPost
        location
        languages
        profileImage
        portfolio
        bio
        isVerified
        availableFrom
        availableTo
        socialMediaHandles {
          instagram { account followerCount }
          youtube { account followerCount }
          tiktok { account followerCount }
          twitter { account followerCount }
          naverblog { account followerCount }
        }
        createdAt
        updatedAt
      }
      count
      total
    }
  }
`;

export const GET_INFLUENCER = `
  query GetInfluencer($influencerId: ID!) {
    getInfluencer(influencerId: $influencerId) {
      influencerId
      userId
      name
      email
      categories
      mainCategory
      engagementRate
      rating
      ratePerPost
      location
      languages
      profileImage
      portfolio
      bio
      isVerified
      availableFrom
      availableTo
      socialMediaHandles {
        instagram { account followerCount }
        youtube { account followerCount }
        tiktok { account followerCount }
        twitter { account followerCount }
        naverblog { account followerCount }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_INFLUENCERS_BY_IDS = `
  query GetInfluencersByIds($influencerIds: [ID!]!) {
    getInfluencersByIds(influencerIds: $influencerIds) {
      influencerId
      userId
      name
      nickname
      email
      mainCategory
      socialMediaHandles {
        instagram { account followerCount }
        youtube { account followerCount }
        naverblog { account followerCount }
      }
      profileImage
    }
  }
`;

export const GET_INFLUENCER_BY_USER_ID = `
  query GetInfluencerByUserId($userId: ID!) {
    getInfluencerByUserId(userId: $userId) {
      success
      message
      error
      data {
        influencerId
        userId
        name
        email
        bio
        mainCategory
        categories
        location
        ratePerPost
        engagementRate
        socialMediaHandles {
          instagram { account followerCount }
          youtube { account followerCount }
          tiktok { account followerCount }
          twitter { account followerCount }
          naverblog { account followerCount }
        }
        languages
        availableFrom
        availableTo
        rating
        isVerified
        totalEarnings
        completedCampaigns
        nickname
        phoneNumber
        phoneVerified
        marketingConsent
        marketingConsentEmail
        marketingConsentSMS
        activityType
        equipment
        maritalStatus
        hasChildren
        bankAccountType
        bankName
        bankAccountHolder
        bankAccountNumber
        rewardBalance
        profileImage
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_CAMPAIGNS = `
  query GetCampaigns($filters: CampaignFiltersInput, $limit: Int, $nextToken: String) {
    getCampaigns(filters: $filters, limit: $limit, nextToken: $nextToken) {
      campaigns {
        campaignId
        hostId
        createdBy
        status
        visibility
        isVerified
        createdAt
        updatedAt
        brandName
        campaignName
        campaignCategory
        representativeImages {
          fileKey
          fileUrl
          fileName
          fileSize
          fileType
        }
        productIntroduction
        productName
        providedValueDescription
        provisionMethod
        additionalNotes
        visitCountry
        visitCity
        visitAddress
        visitLatitude
        visitLongitude
        attachments {
          fileKey
          fileUrl
          fileName
          fileSize
          fileType
        }
        numberOfRecruits
        contentChannel
        contentChannels
        rewardAmount
        contentInsertLink
        recruitmentPeriod {
          startDate
          endDate
          alwaysRecruiting
        }
        announcementStartDate
        announcementEndDate
        alwaysOpening
        visitStartDate
        visitEndDate
        uploadDeadlineDays
        discussSchedule
        contentWritingRequirements
        secondarySubmissions { type quantity note }
        submissionPath
        secondarySubmissionGuide
        advertiserName
        contactPersonName
        campaignType
        contentTypes
        announcementDate
        introductionPageUrl
        publishingMode
        selectedChannels
        publishingConditions {
          instagram {
            collaboratorId
            conditions { item quantity }
          }
          blog {
            insertLink
            conditions { item quantity }
          }
          youtube {
            insertLink
            conditions { item quantity }
          }
        }
        recommendedKeywords
        remarks
      }
      count
      total
      nextToken
    }
  }
`;

export const GET_CAMPAIGNS_BY_HOST = `
  query GetCampaignsByHost($hostId: ID!, $filters: CampaignFiltersInput, $limit: Int) {
    getCampaignsByHost(hostId: $hostId, filters: $filters, limit: $limit) {
      campaigns {
        campaignId
        hostId
        createdBy
        status
        visibility
        isVerified
        createdAt
        updatedAt
        brandName
        campaignName
        campaignCategory
        representativeImages {
          fileKey
          fileUrl
          fileName
          fileSize
          fileType
        }
        productIntroduction
        productName
        providedValueDescription
        provisionMethod
        additionalNotes
        visitCountry
        visitCity
        visitAddress
        visitLatitude
        visitLongitude
        attachments {
          fileKey
          fileUrl
          fileName
          fileSize
          fileType
        }
        numberOfRecruits
        contentChannel
        contentChannels
        rewardAmount
        contentInsertLink
        recruitmentPeriod {
          startDate
          endDate
          alwaysRecruiting
        }
        announcementStartDate
        announcementEndDate
        alwaysOpening
        visitStartDate
        visitEndDate
        uploadDeadlineDays
        discussSchedule
        contentWritingRequirements
        secondarySubmissions { type quantity note }
        submissionPath
        secondarySubmissionGuide
        advertiserName
        contactPersonName
        campaignType
        contentTypes
        announcementDate
        introductionPageUrl
        publishingMode
        selectedChannels
        publishingConditions {
          instagram {
            collaboratorId
            conditions { item quantity }
          }
          blog {
            insertLink
            conditions { item quantity }
          }
          youtube {
            insertLink
            conditions { item quantity }
          }
        }
        recommendedKeywords
        remarks
      }
      count
      total
    }
  }
`;

// Messages Queries - Phase 3
export const GET_MESSAGES = gql`
  query GetMessages($conversationId: ID!, $limit: Int, $nextToken: String) {
    getMessages(conversationId: $conversationId, limit: $limit, nextToken: $nextToken) {
      messages {
        messageId
        conversationId
        senderId
        receiverId
        messageType
        content
        attachments {
          fileId
          fileName
          fileUrl
          fileType
        }
        readStatus {
          isRead
          readAt
        }
        campaignId
        matchId
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_CONVERSATIONS_BY_HOST_ID = gql`
  query GetConversationsByHostId($hostId: ID!, $limit: Int) {
    getConversationsByHostId(hostId: $hostId, limit: $limit) {
      conversations {
        conversationId
        title
        campaignId
        matchId
        hostId
        influencerId
        participants
        lastMessage
        lastMessageAt
        unreadCount {
          userId
          count
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_CONVERSATIONS_BY_INFLUENCER_ID = gql`
  query GetConversationsByInfluencerId($influencerId: ID!, $limit: Int) {
    getConversationsByInfluencerId(influencerId: $influencerId, limit: $limit) {
      conversations {
        conversationId
        title
        campaignId
        matchId
        hostId
        influencerId
        participants
        lastMessage
        lastMessageAt
        unreadCount {
          userId
          count
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_CONVERSATIONS = gql`
  query GetConversations($limit: Int) {
    getConversations(limit: $limit) {
      conversations {
        conversationId
        participants
        campaignId
        matchId
        lastMessageId
        lastMessageAt
        unreadCount {
          userId
          count
        }
        createdAt
        updatedAt
      }
    }
  }
`;

// Notifications Queries - Phase 3
export const GET_NOTIFICATIONS = gql`
  query GetNotifications($limit: Int, $onlyUnread: Boolean) {
    getNotifications(limit: $limit, onlyUnread: $onlyUnread) {
      notifications {
        notificationId
        userId
        type
        title
        message
        relatedEntityType
        relatedEntityId
        actionUrl
        isRead
        readAt
        priority
        createdAt
        updatedAt
      }
      count
    }
  }
`;

export const GET_UNREAD_NOTIFICATION_COUNT = gql`
  query GetUnreadNotificationCount {
    getUnreadNotificationCount {
      count
    }
  }
`;

export const GET_WITHDRAWALS = `
  query GetWithdrawals($userId: ID!) {
    getWithdrawals(userId: $userId) {
      success
      withdrawals {
        withdrawalId
        userId
        amount
        bankName
        bankAccountHolder
        bankAccountNumber
        status
        requestDate
        expectedDate
        completedDate
        createdAt
      }
      count
    }
  }
`;

export const GET_CAMPAIGN = `
  query GetCampaign($campaignId: ID!) {
    getCampaign(campaignId: $campaignId) {
      campaignId
      hostId
      createdBy
      status
      visibility
      isVerified
      createdAt
      updatedAt
      brandName
      campaignName
      campaignCategory
      clientBrandId
      clientBrandName
      representativeImages {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      productIntroduction
      productName
      providedValueDescription
      provisionMethod
      additionalNotes
      visitCountry
      visitCity
      visitAddress
      visitLatitude
      visitLongitude
      attachments {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      numberOfRecruits
      contentChannel
      contentChannels
      rewardAmount
      publishingConditions {
        instagram {
          collaboratorId
          conditions { item quantity }
        }
        blog {
          insertLink
          conditions { item quantity }
        }
        youtube {
          insertLink
          conditions { item quantity }
        }
      }
      contentInsertLink
      instagramCollaboratorId
      blogInsertLink
      youtubeInsertLink
      recruitmentPeriod {
        startDate
        endDate
        alwaysRecruiting
      }
      announcementStartDate
      announcementEndDate
      alwaysOpening
      visitStartDate
      visitEndDate
      uploadDeadlineDays
      discussSchedule
      contentWritingRequirements
      secondarySubmissions { type quantity note }
      submissionPath
      secondarySubmissionGuide
      advertiserName
      contactPersonName
      campaignType
      contentTypes
      announcementDate
      introductionPageUrl
      publishingMode
      selectedChannels
      publishingConditions {
        instagram {
          collaboratorId
          conditions { item quantity }
        }
        blog {
          insertLink
          conditions { item quantity }
        }
        youtube {
          insertLink
          conditions { item quantity }
        }
      }
      recommendedKeywords
      remarks
    }
  }
`;

export const GET_MATCHES = `
  query GetMatches($filters: MatchFiltersInput) {
    getMatches(filters: $filters) {
      matches {
        matchId
        campaignId
        influencerId
        userId
        status
        proposedRate
        coverLetter
        matchScore
        selectedMissions
        visitStatus
        appliedAt
        respondedAt
        completedAt
        createdAt
        updatedAt
      }
      count
      total
    }
  }
`;

export const GET_MATCH = `
  query GetMatch($matchId: ID!) {
    getMatch(matchId: $matchId) {
      matchId
      campaignId
      influencerId
      userId
      status
      proposedRate
      coverLetter
      matchScore
      selectedMissions
      visitStatus
      contentSubmissions {
        seq
        channel
        item
        contentLink
        submittedAt
        status
      }
      visitInfo {
        startDate
        endDate
        adults
        children
        infants
      }
      acceptanceNotes
      appliedAt
      requestedAt
      respondedAt
      completedAt
      createdAt
      updatedAt
    }
  }
`;

// Public queries (no authentication required)
export const GET_PUBLIC_CAMPAIGNS = `
  query GetPublicCampaigns($filters: PublicCampaignFiltersInput) {
    getPublicCampaigns(filters: $filters) {
      campaigns {
        campaignId
        status
        createdAt
        campaignName
        campaignType
        campaignCategory
        contentTypes
        contentChannel
        contentChannels
        representativeImages {
          fileKey
          fileUrl
          fileName
          fileSize
          fileType
        }
        recruitmentPeriod {
          startDate
          endDate
          alwaysRecruiting
        }
        numberOfRecruits
        productName
        providedValueDescription
        rewardAmount
        brandName
        visitCountry
        recommendedKeywords
      }
      count
      total
    }
  }
`;

export const GET_PUBLIC_CAMPAIGN = `
  query GetPublicCampaign($campaignId: ID!) {
    getPublicCampaign(campaignId: $campaignId) {
      campaignId
      status
      createdAt
      campaignName
      campaignType
      campaignCategory
      contentTypes
      contentChannel
      contentChannels
      representativeImages {
        fileKey
        fileUrl
        fileName
        fileSize
        fileType
      }
      recruitmentPeriod {
        startDate
        endDate
        alwaysRecruiting
      }
      numberOfRecruits
      productName
      productIntroduction
      providedValueDescription
      provisionMethod
      additionalNotes
      visitCountry
      visitCity
      visitAddress
      visitLatitude
      visitLongitude
      rewardAmount
      contentInsertLink
      announcementStartDate
      announcementEndDate
      alwaysOpening
      visitStartDate
      visitEndDate
      uploadDeadlineDays
      discussSchedule
      contentWritingRequirements
      secondarySubmissions { type quantity note }
      submissionPath
      secondarySubmissionGuide
      publishingConditions {
        instagram {
          collaboratorId
          conditions { item quantity }
        }
        blog {
          insertLink
          conditions { item quantity }
        }
        youtube {
          insertLink
          conditions { item quantity }
        }
      }
      brandName
      recommendedKeywords
    }
  }
`;

export const GET_PUBLIC_INFLUENCERS = `
  query GetPublicInfluencers($filters: PublicInfluencerFiltersInput) {
    getPublicInfluencers(filters: $filters) {
      influencers {
        influencerId
        name
        bio
        mainCategory
        categories
        location
        followerCounts {
          instagram
          youtube
          tiktok
          twitter
        }
        engagementRate
        rating
        profileImage
        isVerified
      }
      count
      total
    }
  }
`;

export const GET_PUBLIC_INFLUENCER = `
  query GetPublicInfluencer($influencerId: ID!) {
    getPublicInfluencer(influencerId: $influencerId) {
      influencerId
      name
      bio
      mainCategory
      categories
      location
      followerCounts {
        instagram
        youtube
        tiktok
        twitter
      }
      engagementRate
      rating
      profileImage
      portfolio
      isVerified
      completedCampaigns
    }
  }
`;

export const GET_UPLOAD_URL = `
  query GetUploadUrl($fileName: String!, $fileType: String!, $fileSize: Int!, $allowAnyType: Boolean) {
    getUploadUrl(fileName: $fileName, fileType: $fileType, fileSize: $fileSize, allowAnyType: $allowAnyType) {
      uploadUrl
      fileUrl
      fileKey
    }
  }
`;


// ============================================================================
// RBAC Queries
// ============================================================================

export const GET_MY_PERMISSIONS = gql`
  query GetMyPermissions {
    getMyPermissions {
      userId
      roles {
        roleId
        roleName
        description
        permissions
        isSystemRole
      }
      permissions
      effectivePermissions
    }
  }
`;

export const GET_MY_ROLES = gql`
  query GetMyRoles {
    getMyRoles {
      roles {
        roleId
        roleName
        description
        permissions
        isSystemRole
        isActive
        createdAt
      }
      count
    }
  }
`;

export const CHECK_MY_PERMISSION = gql`
  query CheckMyPermission($permission: String!) {
    checkMyPermission(permission: $permission) {
      hasPermission
      permission
      userId
      grantedBy
      deniedReason
    }
  }
`;

export const GET_ALL_ROLES = gql`
  query GetAllRoles($includeInactive: Boolean) {
    getAllRoles(includeInactive: $includeInactive) {
      roles {
        roleId
        roleName
        description
        permissions
        parentRoles
        effectivePermissions
        isSystemRole
        isActive
        metadata {
          displayName
          color
          icon
          category
        }
        createdBy
        createdAt
        updatedAt
      }
      count
    }
  }
`;

export const GET_ROLE = gql`
  query GetRole($roleId: ID!) {
    getRole(roleId: $roleId) {
      roleId
      roleName
      description
      permissions
      parentRoles
      effectivePermissions
      isSystemRole
      isActive
      metadata {
        displayName
        color
        icon
        category
      }
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export const GET_USER_ROLES = gql`
  query GetUserRoles($userId: ID!) {
    getUserRoles(userId: $userId) {
      roles {
        roleId
        roleName
        description
        permissions
        isSystemRole
        isActive
      }
      count
    }
  }
`;

export const GET_USERS_WITH_ROLE = gql`
  query GetUsersWithRole($roleId: ID!) {
    getUsersWithRole(roleId: $roleId) {
      users {
        userId
        email
        name
        userType
      }
      count
    }
  }
`;

export const GET_AUDIT_LOGS = gql`
  query GetAuditLogs($filters: AuditLogFiltersInput) {
    getAuditLogs(filters: $filters) {
      logs {
        auditId
        eventType
        actorId
        targetUserId
        targetRoleId
        changes {
          before
          after
          field
        }
        reason
        metadata {
          ipAddress
          userAgent
          requestId
        }
        timestamp
      }
      count
      nextToken
    }
  }
`;

// ============================================================================
// RBAC Mutations
// ============================================================================

export const CREATE_ROLE = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      roleId
      roleName
      description
      permissions
      parentRoles
      isSystemRole
      isActive
      createdAt
    }
  }
`;

export const UPDATE_ROLE = gql`
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      roleId
      roleName
      description
      permissions
      parentRoles
      isActive
      updatedAt
    }
  }
`;

export const DELETE_ROLE = gql`
  mutation DeleteRole($roleId: ID!) {
    deleteRole(roleId: $roleId) {
      roleId
      roleName
      isActive
    }
  }
`;

export const ASSOCIATE_ROLE = gql`
  mutation AssociateRole($input: AssociateRoleInput!) {
    associateRole(input: $input) {
      associationId
      userId
      roleId
      associatedBy
      associatedAt
      expiresAt
      reason
    }
  }
`;

export const DISSOCIATE_ROLE = gql`
  mutation DissociateRole($input: DissociateRoleInput!) {
    dissociateRole(input: $input) {
      associationId
      userId
      roleId
      reason
    }
  }
`;

export const BULK_ASSOCIATE_ROLES = gql`
  mutation BulkAssociateRoles($input: BulkAssociateRolesInput!) {
    bulkAssociateRoles(input: $input) {
      successCount
      failureCount
      errors
    }
  }
`;

export const GET_CLIENT_BRANDS = `
  query GetClientBrands($userId: ID!) {
    getClientBrands(userId: $userId) {
      success
      brands {
        brandId
        userId
        brandName
        industry
        country
        city
        location
        latitude
        longitude
        website
        contactPerson
        contactEmail
        contactPhone
        campaignCount
        createdAt
        updatedAt
      }
      count
    }
  }
`;

export const GET_NOTICES = `
  query GetNotices($noticeType: String) {
    getNotices(noticeType: $noticeType) {
      notices {
        noticeId
        noticeType
        title
        content
        createdBy
        createdAt
        updatedAt
      }
      count
    }
  }
`;

export const GET_NOTICE = `
  query GetNotice($noticeId: ID!) {
    getNotice(noticeId: $noticeId) {
      noticeId
      noticeType
      title
      content
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export const GET_INQUIRIES = `
  query GetInquiries($userId: ID!) {
    getInquiries(userId: $userId) {
      inquiries {
        inquiryId
        userId
        title
        content
        status
        createdAt
        updatedAt
        answer
        answeredAt
        answeredBy
      }
      count
    }
  }
`;

export const GET_INQUIRY = `
  query GetInquiry($inquiryId: ID!) {
    getInquiry(inquiryId: $inquiryId) {
      inquiryId
      userId
      title
      content
      status
      createdAt
      updatedAt
      answer
      answeredAt
      answeredBy
    }
  }
`;

export const GET_ALL_INQUIRIES = `
  query GetAllInquiries($filter: InquiryAdminFilter, $limit: Int, $nextToken: String) {
    getAllInquiries(filter: $filter, limit: $limit, nextToken: $nextToken) {
      inquiries {
        inquiryId
        userId
        title
        content
        status
        createdAt
        updatedAt
        answer
        answeredAt
        answeredBy
        answeredByName
        requestorName
        requestorUserType
      }
      count
      unansweredCount
      answeredCount
      nextToken
    }
  }
`;

// Document Templates Queries
export const GET_DOCUMENT_TEMPLATES = gql`
  query GetDocumentTemplates {
    getDocumentTemplates {
      docType
      content
      createdAt
      updatedAt
      createdBy
      createdByName
      updatedBy
      updatedByName
    }
  }
`;

// Read-only single-template content for any authenticated user (contract
// preview / confirmation). Avoids the admin-only getDocumentTemplates query.
export const GET_DOCUMENT_TEMPLATE_CONTENT = gql`
  query GetDocumentTemplateContent($docType: String!) {
    getDocumentTemplateContent(docType: $docType) {
      docType
      content
    }
  }
`;
